import { EmptyView } from '@shared/components/ui/EmptyView';
import { useTranslation } from 'react-i18next';
import { ReceptionListItem } from '../../models/Reception';
import { ReceptionAgGridTable } from '../display/ReceptionAgGridTable';
import { ReceptionCardList } from '../display/ReceptionCardList';

interface ReceptionsViewProps {
  receptions: ReceptionListItem[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  total: number;
  // Handlers
  onEdit: (reception: ReceptionListItem) => void;
  onDelete: (reception: ReceptionListItem) => void;
  onCreateReception: () => void;
  // Infinite scroll
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  // Sorting
  sorting: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
  // Filters state
  hasActiveFilters: boolean;
  hasSearchTerm: boolean;
  // Permisos
  canEdit: boolean;
  canDelete: boolean;
}

/**
 * Vista principal de recepciones con tabla/cards según viewport
 */
export const ReceptionsView: React.FC<ReceptionsViewProps> = ({
  receptions,
  isFetching,
  total,
  onEdit,
  onDelete,
  onCreateReception,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sorting,
  onSortingChange,
  hasActiveFilters,
  hasSearchTerm,
  canEdit,
  canDelete,
}) => {
  const { t } = useTranslation('receptionsModule');

  // Mostrar empty state cuando no hay resultados
  if (total === 0) {
    if (hasActiveFilters || hasSearchTerm) {
      return <EmptyView title={t('no_results.title')} description={t('no_results.description')} />;
    }
    return (
      <EmptyView
        title={t('empty_state.title')}
        description={t('empty_state.description')}
        buttonText={t('button.create')}
        onButtonClick={onCreateReception}
      />
    );
  }

  return (
    <div className="relative">
      {/* Vista desktop - Tabla AG Grid */}
      <div className="hidden md:block">
        <ReceptionAgGridTable
          receptions={receptions}
          isFetching={isFetching}
          onEdit={onEdit}
          onDelete={onDelete}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          sorting={sorting}
          onSortingChange={onSortingChange}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      </div>

      {/* Vista móvil - Cards */}
      <div className="block md:hidden">
        <ReceptionCardList
          receptions={receptions}
          onEdit={onEdit}
          onDelete={onDelete}
          hasActiveFilters={hasActiveFilters}
          hasSearchTerm={hasSearchTerm}
          onCreateReception={onCreateReception}
          canEdit={canEdit}
          canDelete={canDelete}
        />

        {/* Botón de cargar más en móvil */}
        {hasNextPage && (
          <div className="flex justify-center py-4 px-4">
            <button
              onClick={fetchNextPage}
              disabled={isFetchingNextPage}
              className="text-sm font-medium text-primary hover:text-primary/80 disabled:opacity-50 transition-colors py-2 px-4 rounded-md hover:bg-primary/5"
            >
              {isFetchingNextPage ? t('button.loading') : t('button.view_details')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceptionsView;
